import { useEffect } from "react"
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { useAuth } from "@/contexts/AuthContext"
import { toast } from "@/utils/toastStyles"

/**
 * Layout route guard for all `/instructor/**` pages.
 *
 * When the user is authenticated but not an instructor (or admin),
 * they are shown an error toast and redirected to the student dashboard.
 * Unauthenticated users are in demo mode and are allowed through.
 */
const InstructorGuard: React.FC = () => {
  const { user, isAuthenticated, isInitializing } = useAuth()
  const navigate = useNavigate()

  const isInstructor = user?.type === "instructor" || user?.role === "admin"
  const isUnauthorized = isAuthenticated && !isInstructor

  useEffect(() => {
    if (isInitializing) return

    if (isUnauthorized) {
      toast.error("You do not have permission to access instructor pages.")
      navigate({ to: "/dashboard", replace: true })
    }
  }, [isInitializing, isUnauthorized, navigate])

  if (isInitializing) {
    return <div className="min-h-screen" />
  }

  if (isUnauthorized) {
    return null
  }

  return <Outlet />
}

export const Route = createFileRoute("/instructor")({
  component: InstructorGuard,
})
